// The specialties filter (office and adviser search, industry feature tabs) is
// driven by the "service-lines" datasource in Storyblok. The datasource has one
// dimension per site, "au" and "nz". An entry is shown on a site unless that
// site's dimension value is set to "null" or "hide", which lets the client keep
// site-specific specialties (e.g. "HR and H&S Consulting" for NZ only).

import type { ISbStoriesParams } from '@storyblok/react'

export const SERVICE_LINES_DATASOURCE = 'service-lines'

export interface ServiceLineEntry {
  name: string
  value: string
  dimension_value?: string | null
}

interface DatasourceClient {
  get: (
    slug: string,
    params: Record<string, unknown>
  ) => Promise<{ data: { datasource_entries?: ServiceLineEntry[] } }>
}

const HIDDEN_DIMENSION_VALUES = new Set(['', 'null', 'hide'])

// NEXT_PUBLIC_NEXT_LOCATION is "en-nz" on the NZ site and "au" (or unset) on AU.
export function serviceLineDimension(location: string | undefined): 'au' | 'nz' {
  return location === 'en-nz' ? 'nz' : 'au'
}

// A missing dimension value (null/undefined) means "no override", so the entry
// is visible. Only an explicit hide marker removes it from this site.
export function isServiceLineVisible(entry: ServiceLineEntry): boolean {
  const dimensionValue = entry.dimension_value
  if (dimensionValue === null || dimensionValue === undefined) return true
  return !HIDDEN_DIMENSION_VALUES.has(dimensionValue.trim().toLowerCase())
}

export function visibleServiceLines(entries: ServiceLineEntry[] | undefined): ServiceLineEntry[] {
  if (!Array.isArray(entries)) return []
  return entries.filter(isServiceLineVisible)
}

// baseParams should be the shared Storyblok request params (searchParamsData),
// which carry the cache version. Without a cv the CDN can serve a stale copy of
// the datasource for days, so a Storyblok edit would not show up on the site.
export async function fetchServiceLines(
  api: DatasourceClient,
  location: string | undefined,
  baseParams: ISbStoriesParams = {}
): Promise<ServiceLineEntry[]> {
  try {
    const { data } = await api.get('cdn/datasource_entries', {
      ...baseParams,
      datasource: SERVICE_LINES_DATASOURCE,
      dimension: serviceLineDimension(location),
      // The endpoint does not sort, so entries are ordered client-side below.
      // One page is enough: the datasource holds ~15 entries and this is not paginated.
      per_page: 100,
    })

    return visibleServiceLines(data.datasource_entries).sort((a, b) => a.name.localeCompare(b.name))
  } catch (error) {
    // A failed request should leave the dropdown empty rather than reject in a
    // component effect where nothing would catch it.
    console.error('Error fetching service lines:', error)
    return []
  }
}
